import { Link } from 'react-router-dom';
import type { Content, ContentType } from '../data/content';
import { useWallet } from '../lib/wallet';
import { cents } from '../lib/format';
import { LockIcon } from './LockIcon';

const TYPE_LABEL: Record<ContentType, string> = {
  article: 'Read',
  photo: 'Photo',
  video: 'Watch',
};

/**
 * A browsable card for micro-unlock content. Shows the type, a price framed as
 * support, and a lock badge until the reader has backed it — then a gold
 * "Unlocked" mark. Preview image (photo/video) or a typographic article tile.
 * Links to /p/:id where the preview → unlock → reveal flow lives.
 */
export function ContentCard({ item }: { item: Content }) {
  const { isUnlocked } = useWallet();
  const unlocked = isUnlocked(item.id);
  const href = `/p/${item.id}`;

  const cover =
    item.preview.type === 'photo'
      ? item.preview.image
      : item.preview.type === 'video'
        ? item.preview.poster
        : null;

  return (
    <article className="group flex flex-col overflow-hidden rounded-xl border border-provny-border bg-provny-surface dark:border-provny-border-dark dark:bg-provny-surface-dark">
      <Link to={href} className="relative block aspect-[4/5] overflow-hidden">
        {cover ? (
          <img
            src={cover}
            alt={item.title}
            loading="lazy"
            className={`h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03] ${
              unlocked ? '' : 'blur-md scale-[1.05]'
            }`}
          />
        ) : item.preview.type === 'article' ? (
          <div className="flex h-full w-full flex-col justify-end bg-gradient-to-br from-provny-surface to-provny-border p-4 dark:from-provny-surface-dark dark:to-provny-ink">
            <p className="font-heading text-lg font-black leading-tight tracking-tight">{item.title}</p>
            <p className="mt-2 line-clamp-3 text-xs text-provny-muted">{item.preview.dek}</p>
          </div>
        ) : null}

        {/* type + price/lock badges */}
        <span className="absolute left-3 top-3 rounded-full border border-white/25 bg-provny-ink/55 px-2 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wider text-white/90 backdrop-blur">
          {TYPE_LABEL[item.type]}
          {item.type === 'video' && item.preview.type === 'video' ? ` · ${item.preview.duration}` : ''}
        </span>
        <span
          className={`absolute right-3 top-3 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.65rem] font-semibold backdrop-blur ${
            unlocked
              ? 'bg-provny-gold text-provny-ink'
              : 'border border-white/25 bg-provny-ink/55 text-white/90'
          }`}
        >
          {unlocked ? (
            'Unlocked'
          ) : (
            <>
              <LockIcon /> {cents(item.priceCents)}
            </>
          )}
        </span>
      </Link>

      <div className="flex flex-1 flex-col gap-2 p-4">
        <span className="text-xs font-medium uppercase tracking-wider text-provny-muted">{item.channel}</span>
        <span className="text-sm text-provny-muted">@{item.creator}</span>
        <Link to={href}>
          <h3 className="font-heading text-lg font-bold leading-snug tracking-tight transition-colors group-hover:text-provny-cyber-dim dark:group-hover:text-provny-cyber">
            {item.title}
          </h3>
        </Link>

        <Link
          to={href}
          className={`mt-auto rounded-xl py-2.5 text-center text-sm font-semibold transition ${
            unlocked
              ? 'border border-provny-border text-provny-muted hover:border-provny-muted dark:border-provny-border-dark'
              : 'bg-provny-cyber text-provny-ink hover:bg-provny-cyber/90'
          }`}
        >
          {unlocked ? 'Open' : `Back this · ${cents(item.priceCents)}`}
        </Link>
      </div>
    </article>
  );
}
